import { defineStore } from 'pinia';

import { accountApiConfig } from '@/config/api/account';
import { usersApiConfig } from '@/config/api/users';
import type { UserBookingRequest } from '@/entities/client/api/booking';
import { getUserBookings } from '@/entities/client/api/booking';
import { useClientApi } from '@/plugins/api/lk';
import { useUserRole } from '@/shared/composables';
import type { ClientWarmthValue } from '@/store/projects';
import type {
  ClientResponse,
  PinStatus,
} from '@/types/Client';

interface CheckedUserResponse {
  agencyId: number;
  agentId: number;
  canDispute: false;
  id: number;
  requested: Date;
  status: PinStatus;
  userId: number;
  button: {
    text: string;
    description: string;
  } | null;
}

interface PinUserResponse {
  agent_id: number;
  agency_id: number;
  id: number;
  bookingId: number;
}

interface PinUserRequest {
  name: string;
  surname: string;
  patronymic: string;
  email: string | null;
  phone: string;
  check_id: number;
  user_id: number;
  active_projects: Array<number>;
  agency_contact: string;
  assignation_comment: string;
  property_type_slug?: string | null;
  consultation_type?: string | null;
  client_warmth?: ClientWarmthValue | null;
  is_promo_lead?: boolean | null;
}

interface State {
  client: ClientResponse | Record<string, never>;
  userData: CheckedUserResponse | null;
  userBooking: Array<UserBookingRequest>;
}

const getDefaultState = (): State => ({
  client: {},
  userData: null,
  userBooking: [],
});

export const useClientStore = defineStore('client', {
  state: (): State => getDefaultState(),

  actions: {
    /**
     * Получение клиента по ID
     */
    async getClient (id: number): Promise<void> {
      try {
        const { role } = useUserRole();
        const { clientDetail } = accountApiConfig.dependentRole;
        const url = clientDetail('users', role, id);
        const { data } = await useClientApi().get<ClientResponse>(url);

        this.client = data;
      } catch (error: unknown) {
        console.log('🚀 ~ file: client.ts ~ getClient ~ error', error);
        this.$sentry.captureException(error);
        throw error;
      }
    },

    /**
     * Проверка клиента на уникальность
     */
    async checkClientUnique (phone: string): Promise<void> {
      try {
        const { role } = useUserRole();
        const { check } = accountApiConfig.dependentRole;
        const url = check('users', role);
        const { data } = await useClientApi().post<CheckedUserResponse>(url, {
          phone,
        });

        this.userData = data;
      } catch (error: unknown) {
        console.log('🚀 ~ file: client.ts ~ checkClientUnique ~ error', error);
        this.$sentry.captureException(error);
        throw error;
      }
    },

    /**
     * Закрепление клиента
     *  @param payload
     *  @param idempotencyKey
     */
    async pinClient (payload: PinUserRequest, idempotencyKey: string): Promise<PinUserResponse> {
      try {
        const { role } = useUserRole();
        const url = usersApiConfig.pin(role);

        const { data } = await useClientApi().post<PinUserResponse>(
          url,
          payload,
          {
            headers: {
              'Idempotency-Key': idempotencyKey,
            },
          },
        );

        return data;
      } catch (error: unknown) {
        console.log('🚀 ~ file: client.ts ~ pinClient ~ error', error);
        this.$sentry.captureException(error);
        throw error;
      }
    },

    /**
     * Оспаривание закрепление клиента
     */
    async checkStartDispute (payload: {
      userId: number;
      comment: string;
    }): Promise<void> {
      try {
        const { role } = useUserRole();
        const { checkDispute } = accountApiConfig.dependentRole;
        const url = checkDispute('users', role);

        await useClientApi().patch(url, payload);
      } catch (error: unknown) {
        console.log('🚀 ~ file: client.ts ~ checkStartDispute ~ error', error);
        this.$sentry.captureException(error);
        throw error;
      }
    },

    reset (): void {
      this.$reset();
    },

    async getBookingByUser (userId: number): Promise<void> {
      this.userBooking = await getUserBookings(userId);
    },
  },
});
